import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useReactToPrint } from "react-to-print";
import { DataTableStateEvent } from "primereact/datatable";
import { SortOrder } from "primereact/api";
import {
	Button,
	Field,
	Label,
	Menu,
	MenuButton,
	MenuItem,
	MenuItems,
	Switch,
} from "@headlessui/react";
import {
	ArrowPathRoundedSquareIcon,
	ChevronDownIcon,
	PlusIcon,
	TrashIcon,
} from "@heroicons/react/24/solid";
import { isHigherThanDistrictAdmin, roleDescriptions, UserRole } from "../../app/userSlice";
import { useAppSelector } from "../../app/hooks";
import AlertMessage, { AlertLevel } from "../../components/AlertMessage";
import ModalFrame from "../../components/ModalFrame";
import SimplePagination from "../../components/SimplePagination";
import { SimpleOption } from "../../components/input/SimpleListbox";
import { Loading } from "../../components/Loading";
import SearchFilter from "../../components/input/SearchFilter";
import Select from "../../components/input/Select";
import PrintDownloadMenu from "../../components/PrintDownloadMenu";
import {
	useGetDistrictsQuery,
	useGetSchoolsByDistrictIdMutation,
} from "../districts/districtsApiSlice";
import UsersListTable from "./UsersListTable";
import PrintUsersTable from "./PrintUsersTable";
import AddUserForm from "./editUserByAdmin/AddUserForm";
import { useUsersListFilters } from "./useUsersListFilters";
import {
	useGetUsersListQuery,
	useManageUserListQuery,
	UsersListQueryModel,
	UserPrintViewModel,
	roleColors,
	useLazyPrintUsersQuery,
} from "./usersApiSlice";

// Maps a sortField name to the legacy iSortCol_0 index expected by the server
// (see UserController.GetPagedUsers in the old WritingWebsites app).
const sortFieldToColIndex: Record<string, number> = {
	userName: 2,
	fullName: 3,
	externalId: 4,
	school: 5,
	role: 6,
	accountStatus: 7,
	// The Last Login Date column uses "loginAtSortValue" (a numeric, client-side-only
	// field) as its sortField so PrimeReact's sort icon compares dates reliably instead
	// of comparing raw ISO strings. It still maps to the same server column index as
	// the underlying "loginAt" field.
	loginAtSortValue: 8,
};

// The old app inverted the sort direction for the Last Login column
// (ascending click resulted in a descending DB sort, and vice versa).
const invertedSortFields = new Set(["loginAtSortValue"]);

export default function UsersList() {
	const { role } = useAppSelector((state) => state.user);

	const _displayLength = 20;

	const {
		page,
		setPage,
		searchFilter,
		setSearchFilter,
		districtFilter,
		setDistrictFilter,
		schoolFilter,
		setSchoolFilter,
		roleFilter,
		setRoleFilter,
		includeInactive,
		setIncludeInactive,
		sortField,
		setSortField,
		sortOrder,
		setSortOrder,
	} = useUsersListFilters();

	// Build the query args used to fetch the table's data. This is initialized
	// lazily (via useState's initializer function) directly from the already-
	// restored (persisted) filter/sort/page values, rather than from static
	// unfiltered defaults. If we instead started from unfiltered defaults and
	// only applied the persisted filters in a useEffect (which runs after the
	// first render), RTK Query would kick off - and briefly display results
	// from - an unfiltered fetch before the effect had a chance to correct it.
	const [queryargs, setQueryargs] = useState<UsersListQueryModel>(() => {
		const colIndex = sortFieldToColIndex[sortField] ?? sortFieldToColIndex["userName"];
		const ascending = sortOrder === -1;
		const effectiveAscending = invertedSortFields.has(sortField) ? !ascending : ascending;

		return {
			iDisplayStart: _displayLength * page,
			iDisplayLength: _displayLength,
			iSortCol_0: colIndex,
			sSortDir_0: effectiveAscending ? "asc" : "desc",
			roleFilter: roleFilter,
			nameFilter: searchFilter,
			showInactiveFilter: includeInactive,
			districtFilter: districtFilter,
			schoolFilter: schoolFilter,
		};
	});
	const [isFiltered, setIsFiltered] = useState(false);

	// The "applied" sort state is what's passed down to the table as its controlled
	// sortField/sortOrder. It's only updated once the corresponding fetch completes,
	// so the table never re-sorts the still-displayed (stale) data by the new column
	// before the freshly server-sorted data arrives.
	const [appliedSortField, setAppliedSortField] = useState(sortField);
	const [appliedSortOrder, setAppliedSortOrder] = useState<SortOrder>(sortOrder);

	const [districtOptions, setDistrictOptions] = useState<SimpleOption[]>([]);
	const [schoolOptions, setSchoolOptions] = useState<SimpleOption[]>([]);
	const [roleOptions, setRoleOptions] = useState<SimpleOption[]>([]);

	const [successMessage, setSuccessMessage] = useState("");
	const [isCreateUserDialogOpen, setIsCreateUserDialogOpen] = useState(false);

	const manageUserListResult = useManageUserListQuery();
	const manage = manageUserListResult.data;

	const userListResult = useGetUsersListQuery(queryargs);
	const users = userListResult.data;

	const getDistrictsResult = useGetDistrictsQuery();
	const districts = getDistrictsResult.data;

	const [callGetSchoolsByDistrict] = useGetSchoolsByDistrictIdMutation();

	const location = useLocation();
	const navigate = useNavigate();

	useEffect(() => {
		if (location.state != null) {
			setIsCreateUserDialogOpen(location.state.openCreateUserDialog ?? false);
		}
		navigate(location, { replace: true });
	}, []);

	useEffect(() => {
		const colIndex = sortFieldToColIndex[sortField] ?? sortFieldToColIndex["userName"];
		const ascending = sortOrder === -1;
		const effectiveAscending = invertedSortFields.has(sortField) ? !ascending : ascending;

		setQueryargs({
			...queryargs,
			iDisplayStart: _displayLength * page,
			roleFilter: roleFilter,
			nameFilter: searchFilter,
			showInactiveFilter: includeInactive,
			districtFilter: districtFilter,
			schoolFilter: schoolFilter,
			iSortCol_0: colIndex,
			sSortDir_0: effectiveAscending ? "asc" : "desc",
		});
	}, [
		page,
		searchFilter,
		districtFilter,
		schoolFilter,
		roleFilter,
		includeInactive,
		sortField,
		sortOrder,
	]);

	const handleSort = (event: DataTableStateEvent) => {
		setSortField(event.sortField);
		setSortOrder(event.sortOrder ?? 1);
		setPage(0);
	};

	// Once the fetch for the newly requested sort has completed, "apply" the pending
	// sort field/order to the table's controlled props. This keeps the table's sort
	// icon in sync with whatever data is currently on screen, preventing PrimeReact
	// from waiting to show the new sort direction until after the stale data updates.
	//
	// These must be the raw (un-inverted) sortField/sortOrder values - i.e. exactly
	// what PrimeReact itself gave us via the onSort event - because PrimeReact uses
	// its own controlled sortField/sortOrder props to calculate the toggle direction
	// the next time the same column is clicked. If we instead fed it the "effective"
	// (server-direction) value for inverted columns like Last Login Date, PrimeReact's
	// toggle math would flip back to the same raw value every other click, so our
	// setSortOrder call would be a no-op and the second click would never re-fetch.
	// The server-direction inversion is applied separately, only when building
	// queryargs above.
	useEffect(() => {
		if (!userListResult.isFetching) {
			setAppliedSortField(sortField);
			setAppliedSortOrder(sortOrder);
		}
	}, [userListResult.isFetching, sortField, sortOrder]);

	// Skip resetting the page on the very first run of this effect (i.e. on mount),
	// since at that point the filter values may have just been restored from the
	// persisted (sessionStorage) state rather than actively changed by the user.
	// Resetting the page in that case would defeat persisting "page" across
	// navigation within the same login session.
	const hasMountedFilters = useRef(false);

	useEffect(() => {
		setIsFiltered(
			!!searchFilter ||
				!!schoolFilter ||
				!!roleFilter ||
				!!districtFilter ||
				!!includeInactive
		);

		if (!hasMountedFilters.current) {
			hasMountedFilters.current = true;
			return;
		}

		setPage(0);
	}, [searchFilter, schoolFilter, roleFilter, districtFilter, includeInactive]);

	const handlePageChange = (page: number) => {
		setPage(page);
	};

	useEffect(() => {
		if (districts) {
			const options = districts.map((d) => ({ value: d.id, label: d.name }) as SimpleOption);
			options.unshift({ value: 0, label: "Any District" } as SimpleOption);

			setDistrictOptions(options);
		}
	}, [districts]);

	useEffect(() => {
		callGetSchoolsByDistrict({
			districtId: districtFilter === 0 ? undefined : districtFilter,
			filterInactive: true,
		})
			.unwrap()
			.then((schools) => {
				if (schools) {
					const options = schools.map(
						(s) => ({ value: s.id, label: s.name }) as SimpleOption
					);
					options.unshift({ value: 0, label: "Any School" } as SimpleOption);

					setSchoolOptions(options);
					setSchoolFilter(0);
				}
			});
	}, [districtFilter]);

	useEffect(() => {
		if (!manage) return;
		const roles = manage.viewableRoles.map((r) => {
			return {
				value: r,
				label: roleDescriptions[r],
				color: roleColors[r],
			} as SimpleOption;
		});
		roles.unshift({ value: "", label: "Any Role" } as SimpleOption);
		setRoleOptions(roles);
	}, [manage]);

	return (
		<section className="print:hidden">
			<h1>Users</h1>
			<AlertMessage
				className="mt-4 text-sm"
				type={AlertLevel.Info}
				message={successMessage}
				showIcon={false}
				onClear={() => setSuccessMessage("")}
			/>
			{role === UserRole.SchoolAdmin ? (
				<div className="mt-4 flex flex-col xl:flex-row gap-4 xl:gap-28 items-end">
					<div className="flex flex-col md:flex-row w-full gap-4 items-end">
						<SearchFilter
							id="searchUsers"
							name="searchUsers"
							searchFilter={searchFilter}
							setSearchFilter={setSearchFilter}
							label="Search"
							placeHolder="Search by name, username or by full external ID..."
							className="w-full md:w-auto flex-1"
							debounceDelay
						/>
						<Select
							value={roleFilter}
							setValue={(v) => setRoleFilter(v as string)}
							label="Role"
							simpleListBoxId="roleFilter"
							options={roleOptions}
							fieldClasses="w-full flex-none md:w-48 pt-2 md:pt-0"
							simpleListBoxClasses="w-full"
						/>
						<Field className="w-full md:w-auto flex-none">
							<Label
								as="span"
								className="block mb-2 text-sm font-medium text-gray-90 pt-2 md:pt-0">
								Include Inactive Users
							</Label>
							<Switch
								checked={includeInactive}
								onChange={setIncludeInactive}
								className="group relative inline-flex h-8 w-14 shrink-0 cursor-pointer rounded-full border-2 border-transparent bg-gray-200 transition-colors duration-200 ease-in-out focus:outline-hidden focus:ring-2 focus:ring-primary-600 focus:ring-offset-2 data-[checked]:bg-primary-600">
								<span
									aria-hidden="true"
									className="pointer-events-none inline-block size-7 transform rounded-full bg-white shadow-sm ring-0 transition duration-200 ease-in-out group-data-[checked]:translate-x-6"
								/>
							</Switch>
						</Field>
					</div>
					<UserListButtons
						queryArgs={queryargs}
						creatableRoles={manage?.creatableRoles ?? []}
						isCreateUserDialogOpen={isCreateUserDialogOpen}
						setSuccessMessage={setSuccessMessage}
						setIsCreateUserDialogOpen={setIsCreateUserDialogOpen}
					/>
				</div>
			) : (
				<>
					<div className="flex flex-col md:flex-row w-full mt-4 gap-4 items-end">
						<Select
							value={districtFilter}
							setValue={(v) => setDistrictFilter(v as number)}
							label="District"
							simpleListBoxId="districtFilter"
							options={districtOptions}
							fieldClasses="w-full md:flex-1"
							simpleListBoxClasses="w-full"
						/>
						<Select
							value={schoolFilter}
							setValue={(v) => setSchoolFilter(v as number)}
							label="School"
							simpleListBoxId="schoolFilter"
							options={schoolOptions}
							fieldClasses="w-full md:flex-1 pt-2 md:pt-0"
							simpleListBoxClasses="w-full"
						/>
						<Select
							value={roleFilter}
							setValue={(v) => setRoleFilter(v as string)}
							label="Role"
							simpleListBoxId="roleFilter"
							options={roleOptions}
							fieldClasses="w-full md:flex-1 pt-2 md:pt-0"
							simpleListBoxClasses="w-full"
						/>
						<Field className="w-full md:w-auto">
							<Label
								as="span"
								className="block mb-2 text-sm font-medium text-gray-90 pt-2 md:pt-0">
								Include Inactive Users
							</Label>
							<Switch
								checked={includeInactive}
								onChange={setIncludeInactive}
								className="group relative inline-flex h-8 w-14 shrink-0 cursor-pointer rounded-full border-2 border-transparent bg-gray-200 transition-colors duration-200 ease-in-out focus:outline-hidden focus:ring-2 focus:ring-primary-600 focus:ring-offset-2 data-[checked]:bg-primary-600">
								<span
									aria-hidden="true"
									className="pointer-events-none inline-block size-7 transform rounded-full bg-white shadow-sm ring-0 transition duration-200 ease-in-out group-data-[checked]:translate-x-6"
								/>
							</Switch>
						</Field>
					</div>
					<div className="flex flex-col md:flex-row w-full mt-4 gap-4 items-end">
						<SearchFilter
							id="searchUsers"
							name="searchUsers"
							searchFilter={searchFilter}
							setSearchFilter={setSearchFilter}
							label="Search"
							placeHolder="Search by name, username or by full external ID..."
							className="w-full md:w-auto flex-1"
							debounceDelay
						/>
						<UserListButtons
							queryArgs={queryargs}
							creatableRoles={manage?.creatableRoles ?? []}
							isCreateUserDialogOpen={isCreateUserDialogOpen}
							setSuccessMessage={setSuccessMessage}
							setIsCreateUserDialogOpen={setIsCreateUserDialogOpen}
						/>
					</div>
				</>
			)}
			<Loading loading={userListResult.isLoading} />
			{users && (
				<UsersListTable
					users={users.aaData}
					handlePageChange={handlePageChange}
					loading={userListResult.isFetching}
					sortField={appliedSortField}
					sortOrder={appliedSortOrder}
					onSort={handleSort}
				/>
			)}

			{((users?.iTotalDisplayRecords ?? 0) > queryargs.iDisplayLength || isFiltered) && (
				<SimplePagination
					itemCount={users?.iTotalDisplayRecords}
					page={page}
					pageSize={queryargs.iDisplayLength}
					totalCount={users?.iTotalRecords}
					isFiltered={isFiltered}
					onChange={(page) => handlePageChange(page)}
					className={"border-none"}
				/>
			)}
		</section>
	);
}

interface UserListButtonsProps {
	queryArgs: UsersListQueryModel;
	creatableRoles: UserRole[];
	isCreateUserDialogOpen: boolean;
	setSuccessMessage: (message: string) => void;
	setIsCreateUserDialogOpen: (open: boolean) => void;
}

function UserListButtons(props: UserListButtonsProps) {
	const { role } = useAppSelector((state) => state.user);

	const [roleToCreate, setRoleToCreate] = useState<string>();

	const roleOptions = props.creatableRoles.map((r) => {
		return {
			value: r,
			label: roleDescriptions[r],
			color: roleColors[r],
		} as SimpleOption;
	});

	function handleCreateRole(newRole: string) {
		setRoleToCreate(newRole);
		props.setIsCreateUserDialogOpen(true);
	}

	return (
		<div className="flex gap-2 items-center">
			<Menu as="div">
				<MenuButton id="add-user-button" className="btn btn-primary">
					<div className="flex items-center">
						<PlusIcon className="size-4 mr-2" /> Add
						<ChevronDownIcon className="size-4 ml-2" aria-hidden="true" />
					</div>
				</MenuButton>
				<MenuItems
					anchor="bottom end"
					className="absolute z-10 pb-1 rounded-md bg-white py-1 shadow-lg ring-1 ring-black/5 focus:outline-none hover:cursor-pointer">
					{roleOptions.map((opt) => {
						return (
							<MenuItem key={`role-${opt.value}`}>
								<a
									onClick={() => handleCreateRole(opt.value as string)}
									className="block data-[focus]:bg-gray-100 px-4 py-2 text-sm text-gray-700">
									<span className="flex items-center">
										{opt.color && (
											<span
												className={`inline-block w-3 h-3 rounded-full mr-2 ${opt.color}`}></span>
										)}
										{opt.label}
									</span>
								</a>
							</MenuItem>
						);
					})}
				</MenuItems>
			</Menu>
			{role && isHigherThanDistrictAdmin(role) && (
				<>
					<Button id="merge-users-button" className="btn btn-neutral" onClick={() => {}}>
						<span className="flex items-center">
							<ArrowPathRoundedSquareIcon className="size-4 mr-2" /> Merge
						</span>
					</Button>
					<Button id="delete-users-button" className="btn btn-neutral" onClick={() => {}}>
						<span className="flex items-center">
							<TrashIcon className="size-4 mr-2" /> Delete
						</span>
					</Button>
				</>
			)}
			<PrintAndDownload queryArgs={props.queryArgs} />

			<ModalFrame
				open={props.isCreateUserDialogOpen}
				title="Create New User"
				overflow="overflow-visible"
				maxWidth="max-w-[750px]"
				onClose={() => props.setIsCreateUserDialogOpen(false)}>
				<AddUserForm
					roleOptions={roleOptions}
					selectedRole={roleToCreate ?? UserRole.Writer}
					setSuccessMessage={props.setSuccessMessage}
					onClose={() => props.setIsCreateUserDialogOpen(false)}
				/>
			</ModalFrame>
		</div>
	);
}

interface PrintAndDownloadProps {
	queryArgs: UsersListQueryModel;
}

function PrintAndDownload({ queryArgs }: PrintAndDownloadProps) {
	const { aspUrl } = useAppSelector((state) => state.site);
	const printRosterRef = useRef<HTMLDivElement>(null);
	const printRoster = useReactToPrint({ contentRef: printRosterRef });
	const [printUsers, setPrintUsers] = useState<UserPrintViewModel[] | null>(null);

	const [callPrintUsers] = useLazyPrintUsersQuery();

	const handlePrintAllUsers = async () => {
		const users = await callPrintUsers(queryArgs).unwrap();
		setPrintUsers(users);
	};

	useEffect(() => {
		if (printUsers && printUsers.length > 0) {
			printRoster();
			setPrintUsers(null);
		}
	}, [printUsers]);

	const queryString = new URLSearchParams({
		iDisplayStart: String(queryArgs.iDisplayStart ?? 0),
		iDisplayLength: String(queryArgs.iDisplayLength ?? 0),
		iSortCol_0: String(queryArgs.iSortCol_0 ?? 0),
		sSortDir_0: queryArgs.sSortDir_0 ?? "asc",
		roleFilter: queryArgs.roleFilter ?? "",
		nameFilter: queryArgs.nameFilter ?? "",
		showInactiveFilter: String(queryArgs.showInactiveFilter ?? false),
		districtFilter: String(queryArgs.districtFilter ?? 0),
		schoolFilter: String(queryArgs.schoolFilter ?? 0),
	}).toString();

	return (
		<>
			<PrintDownloadMenu
				uniqueKey="user-list"
				printLabel="User List"
				onPrint={handlePrintAllUsers}
				downloadExcelLink={`${aspUrl}/User/DownloadExcel?${queryString}`}
				downloadCsvLink={`${aspUrl}/User/DownloadCsv?${queryString}`}
			/>

			<div ref={printRosterRef} className="hidden print:block">
				{printUsers && <PrintUsersTable users={printUsers} />}
			</div>
		</>
	);
}
